import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, useAppStyles } from '../theme';
import { useStore } from '../store';
import toast from 'react-hot-toast';
import { BackBtn, Card, useStatusBar } from './SharedComponents';
import { wallet, exchange, agreements } from '../services/api';


const SOURCE_MAP = {
  'earn':'🎯','VGO_EARN':'🎯','marketplace':'🛒','VGO_COMMERCE':'🛒',
  'VGO_FOOD':'🍕','VGO_FOOD_DELIVERY':'🚚','VGO_SUBSCRIPTIONS':'⭐',
  'subscription':'⭐','transfer':'💸','VGO_PARTNER':'🤝','VGO_REFUND':'↩️',
  'rebalance':'⚖️','REBALANCE':'⚖️','exchanger':'🔄','wallet_merge':'🔗',
};

// ── i18n Currency Map ─────────────────────────────────
const COUNTRY_CURRENCY = {
  'India':{'code':'INR','symbol':'₹','locale':'en-IN','key':'INR'},
  'United States':{'code':'USD','symbol':'$','locale':'en-US','key':'USD'},
  'USA':{'code':'USD','symbol':'$','locale':'en-US','key':'USD'},
  'United Kingdom':{'code':'GBP','symbol':'£','locale':'en-GB','key':'GBP'},
  'UK':{'code':'GBP','symbol':'£','locale':'en-GB','key':'GBP'},
  'UAE':{'code':'AED','symbol':'د.إ','locale':'ar-AE','key':'AED'},
  'United Arab Emirates':{'code':'AED','symbol':'د.إ','locale':'ar-AE','key':'AED'},
  'Nigeria':{'code':'NGN','symbol':'₦','locale':'en-NG','key':'NGN'},
  'Kenya':{'code':'KES','symbol':'KSh','locale':'en-KE','key':'KES'},
  'Germany':{'code':'EUR','symbol':'€','locale':'de-DE','key':'EUR'},
  'France':{'code':'EUR','symbol':'€','locale':'fr-FR','key':'EUR'},
  'Australia':{'code':'AUD','symbol':'A$','locale':'en-AU','key':'AUD'},
  'Canada':{'code':'CAD','symbol':'C$','locale':'en-CA','key':'CAD'},
  'Singapore':{'code':'SGD','symbol':'S$','locale':'en-SG','key':'SGD'},
  'South Africa':{'code':'ZAR','symbol':'R','locale':'en-ZA','key':'ZAR'},
  'Philippines':{'code':'PHP','symbol':'₱','locale':'en-PH','key':'PHP'},
  'Indonesia':{'code':'IDR','symbol':'Rp','locale':'id-ID','key':'IDR'},
  'Malaysia':{'code':'MYR','symbol':'RM','locale':'ms-MY','key':'MYR'},
  'Ghana':{'code':'GHS','symbol':'₵','locale':'en-GH','key':'GHS'},
};
function getCurrencyInfo(country) {
  return COUNTRY_CURRENCY[country] || {'code':'USD','symbol':'$','locale':'en-US','key':'INR'};
}
function fmtCurrency(amount, currInfo) {
  try {
    return new Intl.NumberFormat(currInfo.locale, {
      style:'currency', currency:currInfo.code, maximumFractionDigits:0
    }).format(amount);
  } catch(e) {
    return currInfo.symbol + Math.round(amount).toLocaleString();
  }
}

function TxnDetail({ t, C, inrRate, onClose }) {
  const [closing, setClosing] = React.useState(false);
  const handleClose = () => { setClosing(true); setTimeout(onClose, 300); };
  const isIn = ['inbound','reward_vgc','reward_vgs','escrow_release'].includes(t.type);
  const src  = t.source || '';
  const icon = SOURCE_MAP[src] || null;
  const counterparty = t.counterparty_name || t.description || src.replace('VGO_','').replace(/_/g,' ') || (isIn?'Received':'Sent');
  const TxnIcon = () => {
    if (icon) return <span style={{fontSize:22}}>{icon}</span>;
    const s={width:22,height:22,stroke:isIn?'#10b981':'#ef4444',strokeWidth:2.2,fill:'none',strokeLinecap:'round',strokeLinejoin:'round'};
    if (isIn) return <svg viewBox="0 0 24 24" style={s}><path d="M12 2v14"/><path d="M5 15l7 7 7-7"/></svg>;
    return <svg viewBox="0 0 24 24" style={s}><path d="M12 22V8"/><path d="M5 9l7-7 7 7"/></svg>;
  };
  const amt       = parseFloat(t.amount||0);
  const balAfter  = t.balance_after ? parseFloat(t.balance_after) : null;
  const balBefore = balAfter !== null ? (isIn ? balAfter - amt : balAfter + amt) : null;
  const statusIcon = { completed:'✅', pending:'⏳', failed:'❌' }[t.status||'completed'] || '✅';
  const dateStr = t.date ? new Date(t.date).toLocaleString('en-IN',{
    day:'numeric', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit',
  }) : '';
  const [copied, setCopied] = React.useState('');

  const doCopy = (text, field) => {
    navigator.clipboard && navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
  };

  const copyReceipt = () => {
    const direction = isIn ? 'RECEIVED FROM' : 'PAID TO';
    const sign = isIn ? '+' : '-';
    const text = 
      '=== VGO RECEIPT ===\n' +
      direction + ': ' + counterparty + '\n' +
      'AMOUNT: ' + sign + amt.toFixed(2) + ' TXC\n' +

      'REF: ' + (t.ref_id || '-') + '\n' +
      'DATE: ' + dateStr + '\n' +
      'STATUS: ' + (t.status || 'completed').toUpperCase() + '\n' +
      (t.note ? 'NOTE: ' + t.note + '\n' : '') +
      'TXN: ' + (t.id || '-') + '\n' +
      '=== nexus.vgo.global ===';
    if (navigator.share) {
      navigator.share({ title: 'VGO Receipt', text: text });
    } else {
      navigator.clipboard && navigator.clipboard.writeText(text);
      doCopy(text, 'receipt');
    }
  };

  return (
    <div onClick={handleClose} style={{
      position:'fixed', inset:0, background:closing?'rgba(0,0,0,0)':'rgba(0,0,0,0.6)',
      zIndex:10000, display:'flex', alignItems:'flex-end', justifyContent:'center',
      transition:'background 0.3s ease',
    }}>
      <style>{'@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}} @keyframes slideDown{from{transform:translateY(0)}to{transform:translateY(100%)}}'}</style>
      <div onClick={e=>e.stopPropagation()} style={{
        background:C.bg, borderRadius:'24px 24px 0 0',
        width:'100%', maxWidth:430, maxHeight:'92vh',
        display:'flex', flexDirection:'column',
        animation:closing?'slideDown 0.3s cubic-bezier(0.32,0.72,0,1) forwards':'slideUp 0.32s cubic-bezier(0.32,0.72,0,1)',
      }}>

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px 8px',flexShrink:0}}>
          <button onClick={handleClose} style={{background:C.input,border:'none',borderRadius:12,width:38,height:38,cursor:'pointer',fontSize:18,color:C.text,display:'flex',alignItems:'center',justifyContent:'center'}}>←</button>
          <div style={{fontSize:14,fontWeight:700,color:C.text}}>Transaction Details</div>
          <div style={{width:38}}/>
        </div>

        {/* Scrollable */}
        <div style={{overflowY:'auto',flex:1}}>

          {/* Amount header */}
          <div style={{
            background:isIn?'#0f6e56':'#1a3cc8',
            padding:'20px 20px 32px', textAlign:'center', marginBottom:-18,
          }}>
            <div style={{display:'inline-flex',alignItems:'center',gap:6,background:'rgba(255,255,255,0.2)',borderRadius:20,padding:'4px 14px',fontSize:12,color:'#fff',fontWeight:700,marginBottom:10}}>
              {statusIcon} {(t.status||'completed').toUpperCase()}
            </div>
            <div style={{fontSize:38,fontWeight:900,color:'#fff',letterSpacing:-1}}>
              {isIn?'+':'-'}{Math.round(amt).toLocaleString()}
            </div>
            <div style={{fontSize:14,color:'rgba(255,255,255,0.8)',marginTop:4,fontWeight:600}}>
              TXC &nbsp;·&nbsp; ≈ ₹{(amt*inrRate).toLocaleString('en-IN',{maximumFractionDigits:2})}
            </div>
          </div>

          {/* From/To */}
          <div style={{margin:'0 16px 12px',background:C.card,borderRadius:20,padding:'24px 16px 16px',boxShadow:C.shadow}}>
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <div style={{width:54,height:54,borderRadius:16,flexShrink:0,background:isIn?'#10b98120':'#ef444420',border:'2px solid '+(isIn?'#10b98140':'#ef444440'),display:'flex',alignItems:'center',justifyContent:'center'}}><TxnIcon/></div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:0.5,marginBottom:3}}>{isIn?'Received from':'Paid to'}</div>
                <div style={{fontSize:16,fontWeight:800,color:C.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{counterparty}</div>
                {t.counterparty_id && <div style={{fontSize:11,color:C.muted,marginTop:2}}>{t.counterparty_id}</div>}
              </div>
            </div>
            {t.note && (
              <div style={{marginTop:12,background:C.input,borderRadius:12,padding:'10px 14px',fontSize:12,color:C.muted,fontStyle:'italic'}}>
                💬 {t.note}
              </div>
            )}
          </div>

          {/* Details */}
          <div style={{margin:'0 16px 12px',background:C.card,borderRadius:20,padding:'16px',boxShadow:C.shadow}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:12,letterSpacing:0.3}}>Transaction details</div>
            {[
              {label:'Date & Time',    value:dateStr,                              copy:false},
              {label:'Reference No.',  value:t.ref_id||'—',                       copy:true, field:'ref', full:t.ref_id},
              {label:'TXN ID',         value:t.id?(t.id.slice(0,18)+'…'):'—',    copy:true, field:'id',  full:t.id},
              {label:'Payment Method', value:t.payment_method||'TXC Wallet',      copy:false},
              {label:'Asset',          value:t.asset||'TXC',                       copy:false},
              {label:'Service', value:(src||'—').replace(/_/g,' ').replace(/\w/g,c=>c.toUpperCase()), copy:false},
            ].map((row,i,arr)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'11px 0',borderBottom:i<arr.length-1?('1px solid '+C.border):'none'}}>
                <span style={{fontSize:12,color:C.muted,fontWeight:500,flexShrink:0}}>{row.label}</span>
                <div style={{display:'flex',alignItems:'center',gap:6,flex:1,justifyContent:'flex-end'}}>
                  <span style={{fontSize:12,color:C.text,fontWeight:600,maxWidth:180,textAlign:'right',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{row.value}</span>
                  {row.copy && row.value !== '—' && (
                    <button onClick={()=>doCopy(row.full||row.value,row.field)} style={{background:copied===row.field?'#10b98118':C.input,border:'none',borderRadius:6,padding:'2px 8px',fontSize:10,color:copied===row.field?'#10b981':C.primary,cursor:'pointer',fontWeight:700,flexShrink:0}}>
                      {copied===row.field?'✓':'copy'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Balance impact */}
          {balBefore !== null && (
            <div style={{margin:'0 16px 16px',background:C.card,borderRadius:20,padding:'16px',boxShadow:C.shadow}}>
              <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:14,textTransform:'uppercase',letterSpacing:0.5}}>Balance Impact</div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                <div style={{textAlign:'center',flex:1}}>
                  <div style={{fontSize:11,color:C.muted,marginBottom:4}}>Before</div>
                  <div style={{fontSize:16,fontWeight:800,color:C.text}}>{balBefore.toFixed(2)}</div>
                  <div style={{fontSize:10,color:C.muted}}>TXC</div>
                </div>
                <div style={{flex:2,textAlign:'center'}}>
                  <div style={{background:isIn?'#10b98118':'#ef444418',border:'1px solid '+(isIn?'#10b98133':'#ef444433'),borderRadius:20,padding:'6px 10px',fontSize:14,fontWeight:900,color:isIn?'#10b981':'#fff',display:'inline-block'}}>
                    {isIn?'+':'-'}{Math.round(amt).toLocaleString()}
                  </div>
                  <div style={{display:'flex',alignItems:'center',margin:'6px 0'}}>
                    <div style={{flex:1,height:2,background:C.border}}/>
                    <div style={{fontSize:12,color:C.muted,margin:'0 6px'}}>→</div>
                    <div style={{flex:1,height:2,background:C.border}}/>
                  </div>
                </div>
                <div style={{textAlign:'center',flex:1}}>
                  <div style={{fontSize:11,color:C.muted,marginBottom:4}}>After</div>
                  <div style={{fontSize:16,fontWeight:800,color:isIn?'#10b981':'#fff'}}>{Math.round(balAfter).toLocaleString()}</div>
                  <div style={{fontSize:10,color:C.muted}}>TXC</div>
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div style={{display:'flex',gap:10,padding:'4px 16px 28px'}}>
            <button onClick={copyReceipt} style={{
              flex:1,padding:'13px',background:C.card,
              border:'1px solid '+C.border,borderRadius:14,cursor:'pointer',
              fontSize:12,fontWeight:700,
              color:copied==='receipt'?'#10b981':C.primary,
              display:'flex',alignItems:'center',justifyContent:'center',gap:7,
            }}>
              {copied==='receipt' ? (
                <>
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke="#10b981" strokeWidth="2.5" fill="none" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  Copy Receipt
                </>
              )}
            </button>
            <button onClick={handleClose} style={{
              flex:1,padding:'13px',background:C.primary,
              border:'none',borderRadius:14,color:'#fff',
              fontSize:13,fontWeight:700,cursor:'pointer',
              display:'flex',alignItems:'center',justifyContent:'center',gap:7,
            }}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              Done
            </button>
          </div>
        </div>{/* end scrollable */}

      </div>
    </div>
  );
}

export default function WalletScreen() {
  const C = useTheme();
  useStatusBar('#ffffff');
  const styles = useAppStyles();
  const nav = useNavigate();
  const { user, balance, setBalance } = useStore();
  
  // Refresh balance when page becomes visible again
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        const uid = user?.id || user?.email || 'me';
        wallet.balance(uid).then(b=>setBalance({txc:b.txc_balance||0,vgc:b.vgc_balance||0})).catch(()=>{});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user]);
  const [allTxns, setAllTxns] = useState([]);
  const [page, setPage] = useState(1);
  const [hasExchangers, setHasExchangers] = useState(false);
  const [hasAgreement, setHasAgreement] = useState(false);
  const [walletTab, setWalletTab] = useState('spending');
  const [pocket, setPocket] = useState({txc_balance:0,total_earned:0,total_rewards_received:0});
  const [pocketTxns, setPocketTxns] = useState([]);
  const [myRewards, setMyRewards] = useState([]);
  const [txcPrice, setTxcPrice] = useState(null);

  useEffect(() => {
    const cc = user?.country_code || 'IN';
    fetch(`/api/exchanger/exchanger/list?country_code=${cc}`)
      .then(r=>r.json())
      .then(d=>setHasExchangers((d.exchangers||[]).length > 0))
      .catch(()=>setHasExchangers(false));
    // Check TXC agreement
    agreements.check('txc_utility')
      .then(r=>setHasAgreement(r.accepted||false))
      .catch(()=>setHasAgreement(false));
    // Fetch pocket account
    wallet.pocket && wallet.pocket().then(setPocket).catch(()=>{});
    fetch('/api/backend/v1/pocket',{headers:{Authorization:'Bearer '+localStorage.getItem('nexus_token')}})
      .then(r=>r.json()).then(d=>setPocket(d)).catch(()=>{});
    fetch('/api/backend/v1/pocket/transactions',{headers:{Authorization:'Bearer '+localStorage.getItem('nexus_token')}})
      .then(r=>r.json()).then(d=>setPocketTxns(d.transactions||[])).catch(()=>{});
    fetch('/api/backend/v1/my/rewards',{headers:{Authorization:'Bearer '+localStorage.getItem('nexus_token')}})
      .then(r=>r.json()).then(d=>setMyRewards(d.rewards||[])).catch(()=>{});
    fetch('/api/exchanger/txc/price')
      .then(r=>r.json()).then(setTxcPrice).catch(()=>{});
  }, [user?.country_code]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [rates, setRates] = useState({});
  const [filter, setFilter] = useState('all'); // all | sent | received
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const PAGE_SIZE = 15;
  const uid = user?.username || user?.email?.split('@')[0];

  useEffect(() => {
    if (!uid) return;
    Promise.all([
      wallet.balance(uid).then(b => setBalance({ txc: b.txc_balance||0, vgc: b.vgc_balance||0 })),
      wallet.history(uid).then(r => setAllTxns(r.transactions || [])),
      exchange.rates().then(r => setRates(r.rates || {})),
    ]).catch(()=>{}).finally(() => setLoading(false));
  }, [uid]);

  const filtered = allTxns.filter(t => {
    const isIn = t.type === 'inbound' || t.type === 'reward_vgc' || t.type === 'reward_vgs' || t.type === 'escrow_release';
    if (filter === 'sent' && isIn) return false;
    if (filter === 'received' && !isIn) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (t.description||'').toLowerCase().includes(q) ||
        (t.source||'').toLowerCase().includes(q) ||
        (t.ref_id||'').toLowerCase().includes(q) ||
        String(t.amount||'').includes(q)
      );
    }
    return true;
  });

  const paginated = filtered.slice(0, page * PAGE_SIZE);
  const hasMore   = paginated.length < filtered.length;

  const loadMore = () => {
    setLoadingMore(true);
    setTimeout(() => { setPage(p => p + 1); setLoadingMore(false); }, 300);
  };

  const isInbound = t => ['inbound','reward_vgc','reward_vgs','escrow_release'].includes(t?.type);
  const totalIn  = allTxns.filter(isInbound).reduce((s,t) => s + parseFloat(t.amount||0), 0);
  const totalOut = allTxns.filter(t => !isInbound(t)).reduce((s,t) => s + parseFloat(t.amount||0), 0);

  const inrRate = rates['INR']?.buy_rate || 83.5;
  const currInfo = getCurrencyInfo(user?.country || 'India');
  // TXC price in INR from price feed (correct: ₹0.29/TXC)
  const txcInInr = txcPrice ? parseFloat(txcPrice.price_inr) : 0;
  // Convert to local currency via FX rates (INR as base)
  const fxToLocal = currInfo.key === 'INR' ? 1 : (rates[currInfo.key]?.buy_rate || 1) / (rates['INR']?.buy_rate || 83.5);
  const txcInLocal = txcInInr * fxToLocal;
  const fmtTxcValue = (txc) => fmtCurrency(txc * txcInLocal, currInfo);

  return (
    <div style={{...styles.screen,background:C.bg,paddingBottom:80}}>
      {/* ── BLUE HEADER + BALANCE ── */}
      {/* ── STICKY TOP BAR ── */}
      <div style={{position:'sticky',top:0,zIndex:100,background:'#1a3cc8',
        padding:'14px 16px',display:'flex',alignItems:'center',gap:12}}>
          <button onClick={()=>nav(-1)} style={{width:36,height:36,borderRadius:10,
            background:'rgba(255,255,255,0.15)',border:'none',cursor:'pointer',
            display:'flex',alignItems:'center',justifyContent:'center'}}>
            <svg width="18" height="18" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2.5" fill="none"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          </button>
          <span style={{fontSize:16,fontWeight:700,color:'#fff',flex:1}}>My Wallet</span>
          <div style={{width:36}}/>
      </div>{/* end sticky top bar */}

      {/* ── BLUE BALANCE SECTION ── */}
      <div style={{background:'#1a3cc8',padding:'4px 16px 28px'}}>
        <div style={{textAlign:'center',marginBottom:20}}>
          <div style={{fontSize:11,color:'rgba(255,255,255,0.5)',letterSpacing:1.5,marginBottom:8}}>SPENDING BALANCE</div>
          <div style={{display:'flex',alignItems:'baseline',justifyContent:'center',gap:8}}>
            <span style={{fontSize:52,fontWeight:900,color:'#fff',letterSpacing:-2,lineHeight:1}}>
              {loading?'—':Math.round(balance.txc).toLocaleString('en-IN')}
            </span>
            <span style={{fontSize:18,fontWeight:600,color:'rgba(255,255,255,0.55)'}}>TXC</span>
          </div>
          <div style={{fontSize:14,color:'rgba(255,255,255,0.5)',marginTop:6}}>≈ {fmtTxcValue(balance.txc)}</div>
        </div>
        <div style={{display:'flex',justifyContent:'center',gap:8,marginBottom:20,flexWrap:'wrap'}}>
          {txcPrice&&(()=>{
            const chg=txcPrice.history?.[0]?.change_pct||0; const up=chg>=0;
            return(
              <div style={{background:'rgba(255,255,255,0.12)',borderRadius:20,padding:'5px 12px',display:'flex',alignItems:'center',gap:5}}>
                <span style={{fontSize:12,color:'rgba(255,255,255,0.65)'}}>TXC</span>
                <span style={{fontSize:13,fontWeight:700,color:'#fff'}}>{currInfo.symbol}{(parseFloat(txcPrice.price_inr)*fxToLocal).toFixed(2)}</span>
                <span style={{fontSize:11,fontWeight:600,color:up?'#4ade80':'#f87171'}}>{up?'↑':'↓'}{Math.abs(chg).toFixed(1)}%</span>
              </div>
            );
          })()}
          {!loading&&allTxns.length>0&&<>
            <div style={{background:'rgba(255,255,255,0.12)',borderRadius:20,padding:'5px 12px',display:'flex',alignItems:'center',gap:4}}>
              <span style={{fontSize:11,color:'#4ade80'}}>↓</span>
              <span style={{fontSize:12,color:'rgba(255,255,255,0.7)'}}>+{Math.round(totalIn).toLocaleString()} in</span>
            </div>
            <div style={{background:'rgba(255,255,255,0.12)',borderRadius:20,padding:'5px 12px'}}>
              <span style={{fontSize:12,color:'rgba(255,255,255,0.7)'}}>{allTxns.length} txns</span>
            </div>
          </>}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
          {[
            {l:'Pay',bg:'#fff',color:'#1a3cc8',path:'/transfer',disabled:false},
            {l:'Buy TXC',bg:'rgba(255,255,255,0.15)',color:'#fff',path:hasExchangers?'/inbound':null,disabled:!hasExchangers},
            {l:'Sell TXC',bg:'rgba(255,255,255,0.1)',color:'rgba(255,255,255,0.55)',path:hasExchangers?'/outbound':null,disabled:!hasExchangers},
          ].map(a=>(
            <button key={a.l} disabled={a.disabled} onClick={()=>{
              if(!a.path)return;
              if((a.l==='Buy TXC'||a.l==='Sell TXC')&&!hasAgreement){nav('/txc-agreement');return;}
              nav(a.path);
            }} style={{background:a.bg,border:'none',borderRadius:14,padding:'12px 8px',cursor:a.disabled?'not-allowed':'pointer',
              display:'flex',flexDirection:'column',alignItems:'center',gap:5,opacity:a.disabled?0.5:1}}>
              {a.l==='Pay'&&<svg width="20" height="20" viewBox="0 0 24 24" stroke={a.color} strokeWidth="2" fill="none"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>}
              {a.l==='Buy TXC'&&<svg width="20" height="20" viewBox="0 0 24 24" stroke={a.color} strokeWidth="2" fill="none"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>}
              {a.l==='Sell TXC'&&<svg width="20" height="20" viewBox="0 0 24 24" stroke={a.color} strokeWidth="2" fill="none"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>}
              <span style={{fontSize:12,fontWeight:a.l==='Pay'?700:600,color:a.color}}>{a.l}</span>
            </button>
          ))}
        </div>
      </div>
      {/* ── CONTENT ── */}
      <div style={{padding:'16px 16px 0'}}>
        <div style={{display:'flex',background:C.input,borderRadius:12,padding:3,marginBottom:16,gap:3}}>
          {[['spending','Spending'],['pocket','Pocket']].map(([t,l])=>(
            <button key={t} onClick={()=>setWalletTab(t)} style={{flex:1,padding:'9px',border:'none',borderRadius:10,
              cursor:'pointer',fontSize:13,fontWeight:walletTab===t?700:400,
              background:walletTab===t?'#1a3cc8':'transparent',
              color:walletTab===t?'#fff':C.muted,transition:'all 0.2s'}}>
              {l}{t==='pocket'&&pocket.txc_balance>0?` · ${Math.round(pocket.txc_balance).toLocaleString()}`:''}
            </button>
          ))}
        </div>
      {/* POCKET TAB */}
      {walletTab==='pocket' && (
        <div style={{paddingBottom:80}}>
          {/* Pocket balance card */}
          <div style={{background:'#1a3cc8',borderRadius:18,padding:'18px',marginBottom:16}}>
            <div style={{fontSize:11,color:'rgba(255,255,255,0.65)',letterSpacing:1.5,marginBottom:4}}>POCKET ACCOUNT</div>
            <div style={{fontSize:36,fontWeight:900,color:'#fff',marginBottom:2}}>{Math.round(pocket.txc_balance||0).toLocaleString()} TXC</div>
            <div style={{fontSize:13,color:'rgba(255,255,255,0.8)',marginBottom:12}}>
              ≈ {fmtCurrency((pocket.txc_balance||0) * txcInLocal, currInfo)}
            </div>
            <div style={{display:'flex',gap:8}}>
              <div style={{flex:1,background:'rgba(255,255,255,0.12)',borderRadius:10,padding:'8px 10px'}}>
                <div style={{fontSize:10,color:'rgba(255,255,255,0.6)'}}>Total earned</div>
                <div style={{fontSize:14,fontWeight:700,color:'#fff'}}>{Math.round(pocket.total_earned||0).toLocaleString()} TXC</div>
              </div>
              <div style={{flex:1,background:'rgba(255,255,255,0.12)',borderRadius:10,padding:'8px 10px'}}>
                <div style={{fontSize:10,color:'rgba(255,255,255,0.6)'}}>Rewards</div>
                <div style={{fontSize:14,fontWeight:700,color:'#fff'}}>{Math.round(pocket.total_rewards_received||0).toLocaleString()} TXC</div>
              </div>
            </div>
          </div>
          {/* Asset info */}
          <div style={{background:'#5c35cc12',border:'1px solid #5c35cc30',borderRadius:12,padding:'12px 14px',marginBottom:16,fontSize:12,color:'#5c35cc',lineHeight:1.7}}>
            💎 Asset wallet — quantity never rebalances. Price growth reflects ecosystem revenue. Earns 18% profit distribution periodically.
          </div>
          {/* Sell button */}
          <button onClick={()=>{if(!hasAgreement){nav('/txc-agreement');return;} nav('/outbound');}} style={{width:'100%',background:'#64748b',border:'none',borderRadius:14,padding:'14px',color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer',marginBottom:16}}>
            Sell Pocket TXC
          </button>
          {/* Startup rewards section */}
          {myRewards.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:13,color:C.muted,fontWeight:600,marginBottom:10}}>Startup Rewards ({myRewards.length})</div>
              {myRewards.map((r,i)=>(
                <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px 14px',marginBottom:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{fontSize:22}}>{r.icon}</div>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:C.text}}>{r.startup_name}</div>
                      <div style={{fontSize:11,color:C.muted}}>{r.tokens_held} {r.token_symbol} × {r.reward_per_token} TXC</div>
                      <div style={{fontSize:10,color:C.muted}}>{r.period}</div>
                    </div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:15,fontWeight:700,color:'#10b981'}}>+{r.reward_txc < 1 ? r.reward_txc.toFixed(4) : Math.round(r.reward_txc).toLocaleString()}</div>
                    <div style={{fontSize:10,color:C.muted}}>TXC</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Transaction history */}
          <div style={{fontSize:13,color:C.muted,fontWeight:600,marginBottom:10}}>Pocket Transactions ({pocketTxns.length})</div>
          {pocketTxns.length===0 && (
            <div style={{textAlign:'center',padding:'40px 0',color:C.muted,fontSize:13}}>No pocket transactions yet. Buy digital tokens to earn pocket TXC!</div>
          )}
          {pocketTxns.map((t,i)=>(
            <div key={i} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px 14px',marginBottom:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:C.text,textTransform:'capitalize'}}>{t.tx_type==='earn'?'🪙 Token Purchase Earn':t.ref_id?.startsWith('DIST')?'🌐 Ecosystem Distribution':'🎁 Startup Reward'}</div>
                <div style={{fontSize:11,color:C.muted}}>{t.description}</div>
                <div style={{fontSize:10,color:C.muted}}>{new Date(t.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</div>
              </div>
              <div style={{fontSize:15,fontWeight:700,color:'#10b981'}}>+{Math.round(parseFloat(t.amount)).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}

      {walletTab==='spending' && (
      <div>
      {/* Transaction history */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{fontSize:13,color:C.muted,fontWeight:600}}>
          Transactions {filtered.length > 0 && `(${filtered.length})`}
        </div>
        <div style={{display:'flex',gap:6}}>
          {['all','received','sent'].map(f=>(
            <button key={f} onClick={()=>{setFilter(f);setPage(1);}} style={{
              padding:'5px 10px',
              background:filter===f?C.primary:C.card,
              border:`1px solid ${filter===f?C.primary:C.border}`,
              borderRadius:20,color:filter===f?'#fff':C.muted,
              fontSize:10,fontWeight:600,cursor:'pointer',textTransform:'capitalize'
            }}>{f}</button>
          ))}
        </div>
      </div>

      {/* Search */}
      <input
        placeholder="Search by address, reference..."
        value={search}
        onChange={e=>{setSearch(e.target.value);setPage(1);}}
        style={{...styles.input,marginBottom:14,fontSize:13}}
      />

      {/* Transaction list */}
      {loading ? (
        <div style={{textAlign:'center',padding:48,color:C.muted}}>
          <div style={{
            width:44,height:44,
            border:`4px solid ${C.border}`,
            borderTop:`4px solid ${C.primary}`,
            borderRadius:'50%',
            margin:'0 auto 16px',
            animation:'spin 0.8s linear infinite',
          }}/>
          <div style={{fontSize:13,fontWeight:600}}>Loading transactions...</div>
        </div>
      ) : paginated.length === 0 ? (
        <Card>
          <div style={{textAlign:'center',padding:'28px 0',color:C.muted,fontSize:14}}>
            {search || filter!=='all' ? 'No matching transactions' : 'No transactions yet'}
            {!search && filter==='all' && (
              <div style={{color:C.primary,marginTop:8,display:'block',fontSize:13}}>
                Send or receive TXC to get started
              </div>
            )}
          </div>
        </Card>
      ) : (
        <>
          {paginated.map((t, i) => {
            const isIn = ['inbound','reward_vgc','reward_vgs','escrow_release'].includes(t.type);
            const src = t.source || '';
            const SOURCE_MAP = {
              'earn':'🎯','VGO_EARN':'🎯',
              'marketplace':'🛒','VGO_COMMERCE':'🛒',
              'VGO_FOOD':'🍕','VGO_FOOD_DELIVERY':'🚚',
              'VGO_SUBSCRIPTIONS':'⭐','subscription':'⭐',
              'transfer':'💸','VGO_PARTNER':'🤝',
              'VGO_REFUND':'↩️',
            };
            const icon = SOURCE_MAP[src] || (isIn ? '↓' : '↑');
            const label = t.description || src.replace('VGO_','').replace(/_/g,' ') || (isIn?'Received':'Sent');
            const amt = parseFloat(t.amount||0);
            const timeStr = t.date
              ? new Date(t.date).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})
              : '';
            const dateStr = t.date
              ? new Date(t.date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})
              : '';

            return (
              <div key={i} onClick={()=>setSelectedTxn(t)} style={{
                background:C.card,
                border:`1px solid ${C.border}`,
                borderRadius:16,padding:'14px 16px',
                marginBottom:8,
                display:'flex',alignItems:'center',gap:12,
                boxShadow:C.shadow,
                cursor:'pointer',
              }}>
                {/* Icon */}
                <div style={{
                  width:46,height:46,borderRadius:14,flexShrink:0,
                  background:isIn?'#10b98115':'#ef444415',
                  border:`1.5px solid ${isIn?'#10b98130':'#ef444430'}`,
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:20,
                }}>{icon}</div>

                {/* Details */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:3,
                    overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {label}
                  </div>
                  <div style={{display:'flex',gap:6,alignItems:'center'}}>
                    {t.ref_id && (
                      <span style={{fontSize:10,color:C.muted,background:C.input,
                        padding:'1px 7px',borderRadius:6}}>{t.ref_id}</span>
                    )}
                    <span style={{fontSize:10,color:C.muted}}>{dateStr} {timeStr}</span>
                  </div>
                </div>

                {/* Amount */}
                <div style={{textAlign:'right',flexShrink:0}}>
                  <div style={{fontSize:15,fontWeight:800,
                    color:isIn?'#10b981':'#ef4444'}}>
                    {isIn?'+':'-'}{Math.round(amt).toLocaleString()}
                  </div>
                  <div style={{fontSize:10,color:C.muted,marginTop:1}}>TXC</div>
                </div>
              </div>
            );
          })}

          {/* Load more */}
          {hasMore && (
            <button onClick={loadMore} disabled={loadingMore} style={{
              width:'100%',padding:'14px',
              background:C.card,border:`1px solid ${C.border}`,
              borderRadius:12,color:loadingMore?C.muted:C.primary,
              fontSize:14,fontWeight:600,cursor:'pointer',marginTop:4
            }}>
              {loadingMore
                ? 'Loading...'
                : `Load more (${filtered.length - paginated.length} remaining)`}
            </button>
          )}

          {!hasMore && filtered.length > PAGE_SIZE && (
            <div style={{textAlign:'center',fontSize:12,color:C.muted,padding:'12px 0'}}>
              All {filtered.length} transactions shown
            </div>
          )}
        </>
        )}
      </div>
      )}
      </div>{/* end content padding */}
      {selectedTxn && <TxnDetail t={selectedTxn} C={C} inrRate={inrRate} onClose={()=>setSelectedTxn(null)} />}
    <div style={{height:16}}/>
    </div>
  );
}
